import { FC, useEffect, useState } from 'react';
import {
  FormControlLabel,
  MenuItem,
  Select,
} from '@mui/material';
import {
  Controller,
  ControllerRenderProps,
  FieldValues,
  useFormContext,
} from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faXmark } from '@fortawesome/pro-light-svg-icons';
import classNames from 'classnames';

import { ColorTheme } from 'enum/ColorTheme';

import './AppSelect.scss';

const DROPDOWN_HEIGHT = 180;

interface IComponentProps {
  name?: string;
  required?: boolean;
  disabled?: boolean;
  multiline?: boolean;
  fieldStyle?: any;
  formItemName?: string;
  error?: boolean;
  helperText?: string;
  options: any;
  onChangeValue?: (value: any) => void;
  formatValue?: (value: any) => void;
  initialValue?: string;
  readOnly?: boolean;
  className?: string;
  supportClear?: boolean;
  dataTestId?: string;
  selectedValue?: string;
  theme?: ColorTheme;
}

const AppSelect: FC<IComponentProps> = ({
  name,
  required = false,
  disabled = false,
  fieldStyle,
  formItemName = '',
  options,
  onChangeValue,
  formatValue,
  initialValue,
  readOnly = false,
  className = '',
  supportClear = false,
  dataTestId = '',
  selectedValue,
  theme = ColorTheme.BLUE,
}) => {
  const ctx = useFormContext();
  const field = ctx?.register?.(formItemName, { required });

  const [controlledValue, setControlledValue] = useState<any>();

  useEffect(() => {
    if (initialValue) {
      setControlledValue(initialValue);
    }
  }, [initialValue]);

  useEffect(() => {
    if (selectedValue) {
      setControlledValue(selectedValue);
    }
  }, [selectedValue]);

  const getSelectForm = (fieldState?: ControllerRenderProps<FieldValues, string>) => (
    <FormControlLabel
      className={`app-select ${className}`}
      required={required}
      control={(
        <Select
          defaultValue={initialValue}
          value={(fieldState ? fieldState.value : controlledValue) || ''}
          required={required}
          {...(fieldState ? field : {})}
          readOnly={readOnly}
          disabled={disabled}
          IconComponent={(props) => {
            return <FontAwesomeIcon
              className={classNames('MuiSelect-icon', { hidden: readOnly }, props.className)}
              icon={faAngleDown}
            /> as any;
          }}
          endAdornment={(
            supportClear && (
              <FontAwesomeIcon
                className={classNames('remove', { disabled })}
                icon={faXmark}
                onClick={() => {
                  if (fieldState) {
                    fieldState.onChange('');
                  } else {
                    setControlledValue('');
                  }
                  onChangeValue?.('');
                }}
              />
            )
          )}
          sx={fieldStyle}
          data-testid={dataTestId}
          onChange={(e) => {
            const { value } = e.target;
            if (fieldState) {
              fieldState.onChange(e);
            } else {
              setControlledValue(value);
            }
            onChangeValue?.(value);
          }}
          onClose={() => {
            if (!readOnly) {
              setTimeout(() => {
                (document.activeElement as HTMLElement)?.blur();
              }, 0);
            }
          }}
          MenuProps={{
            PaperProps: {
              sx: { maxHeight: DROPDOWN_HEIGHT },
              className: `app-select__paper ${theme}`,
            },
          }}
        >
          {options?.map((option: string) => (
            <MenuItem key={option} value={option}>
              {formatValue?.(option) || option}
            </MenuItem>
          ))}
        </Select>
      )}
      label={name}
      labelPlacement="top"
    />
  );

  return (
    formItemName ? (
      <Controller
        name={formItemName}
        control={ctx?.control}
        render={({ field }) => getSelectForm(field)}
      />
    ) : getSelectForm()
  );
};

export default AppSelect;
